<script lang="ts" setup>
import { Splide, SplideSlide, SplideTrack } from "@splidejs/vue-splide";
import type { PropType } from "vue";
import { ref } from "vue";
import LazyImage from "@/components/Image/LazyImage.ce.vue";
import type ViewSliderImageFunnelEvent from "@/types/funnel/viewSliderImageFunnelEvent";
import { VIEW_SLIDER_IMAGE } from "@/globalConsts/searchFunnelEvents";
import { pushToDataLayer } from "@/lib/gtm/dataLayer";

const props = defineProps({
    imgUrl: {
        type: String,
        required: true,
    },
    sliderImages: {
        type: Array as PropType<string[]>,
        default: () => [],
        required: true,
    },
    altText: {
        type: String,
        required: true,
    },
    itemId: {
        type: Number,
        required: true,
    },
});

const prevNextDot = ref("splide__pagination--first-dot-large");

function getPrevIndex(newIndex: number, prevIndex: number) {
    if (prevIndex <= 1) {
        prevNextDot.value = "splide__pagination--first-dot-large";
    } else if (prevIndex >= props.sliderImages.length - 2) {
        prevNextDot.value = "splide__pagination--last-dot-large";
    } else {
        prevNextDot.value = "";
    }
    pushToDataLayer({
        event: VIEW_SLIDER_IMAGE,
        item_id: props.itemId,
        position: prevIndex + 1,
    } as ViewSliderImageFunnelEvent);
}
</script>
<template>
    <Splide
        :has-track="false"
        v-if="sliderImages.length > 0"
        @splide:move="getPrevIndex"
    >
        <SplideTrack>
            <SplideSlide
                v-for="(sliderImage, index) in sliderImages"
                :key="'sliderImage_' + index"
            >
                <lazy-image :img-url="sliderImage" :alt-text="altText" />
            </SplideSlide>
        </SplideTrack>
        <div class="[ splide__arrows ]">
            <button
                class="[ splide__arrow splide__arrow--prev ]"
                @click.stop.prevent
            >
                <span
                    class="[ c-icon c-icon--medium ] [ icon-arrow--left ]"
                ></span>
            </button>
            <button
                class="[ splide__arrow splide__arrow--next ]"
                @click.stop.prevent
            >
                <span
                    class="[ c-icon c-icon--medium ] [ icon-arrow--right ]"
                ></span>
            </button>
        </div>
        <ul
            class="splide__pagination splide__pagination--ltr"
            :class="prevNextDot"
            role="tablist"
            aria-label="Select a slide to show"
        ></ul>
    </Splide>
    <Splide :has-track="false" v-else-if="imgUrl !== ''">
        <SplideTrack>
            <SplideSlide>
                <lazy-image :alt-text="altText" :img-url="imgUrl" />
            </SplideSlide>
        </SplideTrack>
    </Splide>
</template>
